import React, { useEffect, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import {
  GetContactInformationMyMSCResponse,
  UpdateContactInformationMyMSCRequest,
} from '@msc-b2c/interfaces/interfaces';
import { ApiErrorModalProps, ChildInput, SpecificErrorMessage } from '@msc-b2c/interfaces/types';
import {
  closeLoader,
  FormTypeEnum,
  getActiveSession,
  openLoader,
  RootState,
  setDeepValue,
  UpdateContactInformationMyMSC,
} from '@msc-b2c/shared-utils';
import { Button, getFormChildren, Modal, Text } from '@msc-b2c/shared-components';
import { buildConsentPerChannel, getFormInputsDefaultValues } from '../helpers/helpers';
import { getErrorContent } from '../../utils/apiUtils';
import { useTransformFormData } from '../hooks/hooks';
import { useSelector } from 'react-redux';

export function CustomerInformationPopUp({
  id,
  iconType,
  title,
  submitLabel,
  cancelLabel,
  modalType,
  customerInfo,
  formElementsPopUp = [],
  goldenId,
  handleClosePopUp,
  setAreUserDataOutdated,
  setError,
  errors,
  enableCheckboxTitleAndSubtitle = false,
  privacyAgreementText,
}: Readonly<{
  id: string;
  iconType: string;
  title: string;
  submitLabel: string;
  cancelLabel: string;
  modalType: string;
  customerInfo?: GetContactInformationMyMSCResponse;
  formElementsPopUp?: ChildInput[];
  goldenId?: string;
  verifyZipCodeButtonEnabled?: boolean;
  verifyZipCodeButtonLabel?: string;
  handleClosePopUp: () => void;
  setAreUserDataOutdated: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<ApiErrorModalProps | null>>;
  errors?: SpecificErrorMessage;
  enableCheckboxTitleAndSubtitle?: boolean;
  privacyAgreementText?: string;
}>) {
  const { countryCode } = useSelector(
    (state: RootState) => state.genericConfigs.configs.b2cCommerceConfig
  );

  const formFieldsWithAddressRules = useMemo(() => {
    const hasSavedCountry = !!customerInfo?.address?.country?.trim() || !!countryCode;
    return formElementsPopUp.map((field) => {
      const isCountry =
        field.type === FormTypeEnum.DROPDOWN &&
        (field.name === 'address.country' || field.name === 'address.countryCode');
      if (!isCountry) return field;

      if (!customerInfo?.address?.country && !!countryCode) {
        field.value = countryCode;
      }

      return {
        ...field,
        isCoRPrefilledAndDisabled: false,
        readOnly: hasSavedCountry,
      };
    });
  }, [formElementsPopUp, customerInfo?.address?.country]);

  const defaultValues = getFormInputsDefaultValues(customerInfo, formFieldsWithAddressRules);
  const formMethods = useForm({
    shouldFocusError: false,
    defaultValues,
  });
  const { reset } = formMethods;
  const stableFormElements = formFieldsWithAddressRules.filter(
    (item) => !item.isCoRPrefilledAndDisabled
  );
  const transformedFormElements = useTransformFormData(
    formFieldsWithAddressRules.filter((item) => item.isCoRPrefilledAndDisabled),
    formMethods.setValue,
    formMethods.resetField
  );

  useEffect(() => {
    document.body.classList.add('-overlay');
    return () => {
      document.body.classList.remove('-overlay');
    };
  }, []);

  useEffect(() => {
    if (!customerInfo || !formFieldsWithAddressRules.length) return;
    reset(getFormInputsDefaultValues(customerInfo, formFieldsWithAddressRules));
  }, [customerInfo, formFieldsWithAddressRules, modalType, reset]);

  const onSubmitForm = async (data: { [key: string]: any }) => {
    openLoader('.loader');

    const promises: Promise<void>[] = [];
    delete data.prefix;

    const body: UpdateContactInformationMyMSCRequest & { [key: string]: any } = {
      name: customerInfo?.name ?? '',
      surname: customerInfo?.surname ?? '',
      dateOfBirth: customerInfo?.dateOfBirth ?? '',
      email: data?.email ?? customerInfo?.email ?? getActiveSession().email ?? '',
      ...(data?.phone
        ? { phone: data.phone }
        : customerInfo?.phone
        ? { phone: customerInfo.phone }
        : {}),
      ...(data?.mobilePhone
        ? { mobilePhone: data.mobilePhone }
        : customerInfo?.mobilePhone
        ? { mobilePhone: customerInfo.mobilePhone }
        : {}),
      ...(data?.address
        ? {
            address: {
              ...(data?.address?.country ? { country: data?.address?.country } : {}),
              ...(data?.address?.addressLine1
                ? {
                    addressLine1: data?.address?.addressLine1,
                  }
                : {}),
              ...(data?.address?.postCode ? { postCode: data?.address?.postCode } : {}),
              ...(data?.address?.province ? { province: data?.address?.province } : {}),
              ...(data?.address?.region ? { region: data?.address?.region } : {}),
              ...(data?.address?.state ? { state: data?.address?.state } : {}),
              ...(data?.address?.town ? { town: data?.address?.town } : {}),
            },
          }
        : customerInfo?.address
        ? {
            address: {
              ...(customerInfo?.address?.country
                ? { country: customerInfo?.address?.country }
                : {}),
              ...(customerInfo?.address?.addressLine1
                ? {
                    addressLine1: customerInfo?.address?.addressLine1,
                  }
                : {}),
              ...(customerInfo?.address?.postCode
                ? { postCode: customerInfo?.address?.postCode }
                : {}),
              ...(customerInfo?.address?.province
                ? { province: customerInfo?.address?.province }
                : {}),
              ...(customerInfo?.address?.region ? { region: customerInfo?.address?.region } : {}),
              ...(customerInfo?.address?.state ? { state: customerInfo?.address?.state } : {}),
              ...(customerInfo?.address?.town ? { town: customerInfo?.address?.town } : {}),
            },
          }
        : {}),
      ...(() => {
        const touchedFields = Object.keys(formMethods.formState.touchedFields);
        const hasEmail = !!(data?.email ?? customerInfo?.email ?? getActiveSession().email);
        const hasPhone = !!(data?.phone ?? customerInfo?.phone);
        const hasMobilePhone = !!(data?.mobilePhone ?? customerInfo?.mobilePhone);

        const marketingTouched = touchedFields.includes('marketingConsent');
        const marketingConsents = buildConsentPerChannel(
          marketingTouched,
          marketingTouched ? data?.marketingConsent === true : undefined,
          customerInfo?.marketingConsents,
          hasEmail,
          hasPhone,
          hasMobilePhone
        );

        const profilingTouched = touchedFields.includes('profilingConsents');
        const profilingConsents = buildConsentPerChannel(
          profilingTouched,
          profilingTouched ? data?.profilingConsents === true : undefined,
          customerInfo?.profilingConsents,
          hasEmail,
          hasPhone,
          hasMobilePhone
        );

        return {
          ...(marketingConsents !== undefined ? { marketingConsents } : {}),
          ...(data?.thirdPartyConsent !== undefined && touchedFields.includes('thirdPartyConsent')
            ? { thirdPartyConsent: data.thirdPartyConsent === true }
            : customerInfo?.thirdPartyConsent !== undefined
            ? { thirdPartyConsent: customerInfo.thirdPartyConsent }
            : {}),
          ...(profilingConsents !== undefined ? { profilingConsents } : {}),
        };
      })(),
      privacyConsent: true,
    };

    if (body?.marketingConsent) delete body.marketingConsent;

    Object.entries(body).forEach(([key, value]) => {
      promises.push(setDeepValue(body, key, value as FormDataEntryValue));
      if (value == null || value === '') delete body[key as keyof typeof body];
    });

    await Promise.all(promises);

    const request = new UpdateContactInformationMyMSC(false, false, body, {
      goldenId: goldenId ?? '',
    });

    request
      .sendData()
      .then(() => {
        setAreUserDataOutdated(true);
      })
      .catch((error) => {
        console.error('Error: ', error);
        const { title, message } = getErrorContent(error, errors);
        setError({ message, title, iconType: errors?.errorMessageIcon ?? '' });
      })
      .finally(() => {
        closeLoader('.loader');
        handleClosePopUp();
      });
  };

  return (
    <Modal
      id={id}
      styleOption="-modal-private-area"
      modalIcon={iconType}
      onClose={handleClosePopUp}
    >
      <div className="modal-header">
        <Text
          text={title}
          styleOption="cmp-text__big bold"
        />
      </div>
      <form
        className="cmp-container__modal-form"
        data-mymsc-customerinfo-modal-form=""
        onSubmit={formMethods.handleSubmit(onSubmitForm as SubmitHandler<FieldValues>)}
      >
        {formFieldsWithAddressRules &&
          (modalType === 'PRIVACY' ? (
            <div className="cmp-form-options cmp-form-options--checkbox-container">
              {getFormChildren(stableFormElements, formMethods, { enableCheckboxTitleAndSubtitle })}
              {getFormChildren(transformedFormElements, formMethods, {
                enableCheckboxTitleAndSubtitle,
              })}
            </div>
          ) : (
            <>
              {getFormChildren(stableFormElements, formMethods, { enableCheckboxTitleAndSubtitle })}
              {getFormChildren(transformedFormElements, formMethods, {
                enableCheckboxTitleAndSubtitle,
              })}
            </>
          ))}
        <div
          className="cmp-container__modal-buttons"
          data-mymsc-customerinfo-modal-actions=""
        >
          {(!!privacyAgreementText && (
            <div className={'cmp-container__modal-buttons-text'}>
              <Text
                text={privacyAgreementText}
                styleOption={''}
              />
            </div>
          )) || (
            <Button
              styleOption="cmp-button__secondary"
              buttonType="button"
              text={cancelLabel}
              accessibilityLabel={cancelLabel}
              disabled={false}
              onClick={handleClosePopUp}
            />
          )}
          <Button
            styleOption="cmp-button__primary"
            buttonType="submit"
            text={submitLabel}
            accessibilityLabel={submitLabel}
            disabled={!formMethods.formState.isValid}
          />
        </div>
      </form>
    </Modal>
  );
}
